/**
 * ErrorBoundary - catches render-time throws so a bug in one view cannot blank
 * the whole app.
 *
 * Before this existed, any component that threw during render produced a blank
 * white screen: React unmounts the tree and, with no boundary anywhere in the
 * app, there was nothing left to paint. This renders the shared `crash`
 * treatment instead and offers a real recovery.
 *
 * Two scopes, both from this one class:
 *
 *   // route level - wraps a whole screen (router `errorElement` equivalent)
 *   <ErrorBoundary><SomeRoute /></ErrorBoundary>
 *
 *   // section level - keeps the rest of the page alive when one panel throws
 *   <ErrorBoundary scope="section" noun="the trust graph">
 *     <TrustGraphPanel />
 *   </ErrorBoundary>
 *
 * `scope="section"` renders the inline banner (treatment B) because the
 * surrounding page is still intact and usable; `scope="route"` renders the
 * full-page card (treatment A) because nothing else survived.
 *
 * Reset semantics: "Reload view" clears the captured error and re-mounts the
 * subtree via a key bump, so a transient render bug recovers without a full
 * page reload. `resetKeys` additionally clears the error whenever a value
 * changes (pass the route path or an id so navigating away un-sticks it).
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { captureError } from '@shared/analytics/capture';
import { ErrorState } from '../PageState';
import { InlineError } from '../InlineError';

export type ErrorBoundaryProps = {
  children: ReactNode;
  /** `route` renders the full-page card; `section` renders an inline banner. */
  scope?: 'route' | 'section';
  /** Names the failing area in the copy ("the trust graph"). */
  noun?: string;
  /**
   * When any of these values changes, the boundary clears its error. Pass the
   * current pathname so navigation recovers a stuck view.
   */
  resetKeys?: ReadonlyArray<unknown>;
  /** Escape hatch for a bespoke fallback. Receives the thrown error. */
  fallback?: (error: unknown, reset: () => void) => ReactNode;
  /** Side-channel for logging/telemetry. Must not throw. */
  onError?: (error: unknown, info: ErrorInfo) => void;
};

type ErrorBoundaryState = {
  error: unknown;
  /** Bumped on reset so the subtree remounts from scratch. */
  attempt: number;
};

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public state: ErrorBoundaryState = { error: null, attempt: 0 };

  public static getDerivedStateFromError(error: unknown): Partial<ErrorBoundaryState> {
    return { error };
  }

  public componentDidCatch(error: unknown, info: ErrorInfo): void {
    // Keep the stack in the console. Without this the only trace of a caught
    // crash is the fallback UI, which makes debugging much harder.
    console.error('[ErrorBoundary] render error', error, info.componentStack);
    captureError(error, { boundary: this.props.scope ?? 'route' });
    try {
      this.props.onError?.(error, info);
    } catch {
      // A throwing reporter must not replace the original error.
    }
  }

  public componentDidUpdate(prev: ErrorBoundaryProps): void {
    if (this.state.error === null) return;
    if (!changed(prev.resetKeys, this.props.resetKeys)) return;
    this.reset();
  }

  private readonly reset = (): void => {
    this.setState((s) => ({ error: null, attempt: s.attempt + 1 }));
  };

  public render(): ReactNode {
    const { error, attempt } = this.state;
    const { children, scope = 'route', noun, fallback } = this.props;

    if (error === null) {
      // The key bump is what makes "Reload view" a real remount rather than a
      // no-op: a fresh key discards the subtree's previous state.
      return (
        <div key={attempt} className="contents">
          {children}
        </div>
      );
    }

    if (fallback) return fallback(error, this.reset);

    if (scope === 'section') {
      return <InlineError error={error} noun={noun} onRetry={this.reset} />;
    }

    return <ErrorState error={error} noun={noun} onRetry={this.reset} />;
  }
}

/** Shallow compare for `resetKeys`. */
function changed(a: ReadonlyArray<unknown> = [], b: ReadonlyArray<unknown> = []): boolean {
  if (a.length !== b.length) return true;
  return a.some((value, i) => !Object.is(value, b[i]));
}

export default ErrorBoundary;
